import React, { useState } from 'react';
import { sortBy } from 'lodash';

import { useDispatch, useSelector } from 'react-redux';
import { setAllFeaturesSelected, setFeatureSelected, setOrder, setOrderBy } from '../../../../../redux/analysis/Actions';
import { highlightFeatures, unhighlightFeatures } from '../../../../../redux/highlights/Actions';
import { setFitBounds } from '../../../../../redux/map/Actions';
import { MAIN_MAP } from '../../../../../redux/map/Constants';

import {
    Checkbox,
    FormControlLabel,
    MenuItem,
    Typography,
    makeStyles
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import ScrollableContainer from '../../../../utility/ScrollableContainer';
import SimpleSelect from '../../common/SimpleSelect';
import AnalysisCard from './AnalysisCard';
import CreateSiteCardDialog from '../../../../Common/SiteCard/CreateDialog';

const useStyles = makeStyles((theme) => styles(theme));

function AnalysisList () {
    const classes = useStyles();
    const dispatch = useDispatch();

    const analysis = useSelector(state => state.analysis.analysis);

    const layerId = useSelector(state => state.search.searchArea.layerId);
    const layerSystemName = useSelector(state => state.layers.layers.find(el => el.id === layerId).system_name);
    const orderBy = useSelector(state => state.analysis.orderBy);
    const order = useSelector(state => state.analysis.order);
    const selectedFeatures = useSelector(state => state.analysis.selectedFeatures);
    const siteCards = useSelector(state =>  state.siteCard.siteCards);

    function dispatchSetOrderBy (orderBy) { dispatch(setOrderBy(orderBy)); }
    function dispatchSetOrder (order) { dispatch(setOrder(order)); }
    function dispatchSetFeatureSelected (id, isSelected) { dispatch(setFeatureSelected(id, isSelected)); }
    function dispatchSetAllFeaturesSelected (isSelected) { dispatch(setAllFeaturesSelected(isSelected)); }
    function dispatchHighlightFeature (highlight) { dispatch(highlightFeatures([highlight])); }
    function dispatchUnhighlightFeature (highlight) { dispatch(unhighlightFeatures([highlight])); }
    function dispatchSetFitBounds (bounds, options = {}) { dispatch(setFitBounds(bounds, options, MAIN_MAP)); }

    const [isCreateSiteCardDialogOpen, setIsCreateSiteCardDialogOpen] = useState(false);
    const [createSiteCardFeatureAnalysisId, setCreateSiteCardFeatureAnalysisId] = useState(null);

    // ToDo on component unmount, remove highlight layers
    function handleOrderByChange (event) {
        dispatchSetOrderBy(event.target.value);
    }

    function handleOrderChange (event) {
        dispatchSetOrder(event.target.value);
    }

    function handleAllSelected (event) {
        dispatchSetAllFeaturesSelected(event.target.checked);
    }

    function openCreateSiteCardDialog (featureAnalysisId) {
        setIsCreateSiteCardDialogOpen(true);
        setCreateSiteCardFeatureAnalysisId(featureAnalysisId);
        // TODO
        // Both sets of logic here should be more generic as could appear in another place

        // If sitecard doesn't exist prompt dialog to create with name
        // Then set sitecard id on feature analyses in backend

        // Else sitecard already exists so just attach feature analyses on backend
    }

    function closeCreateSiteCardDialog () {
        setIsCreateSiteCardDialogOpen(false);
        setCreateSiteCardFeatureAnalysisId(null);
    }

    function handleView () {
        // If sitecard already exists then go to on dashboard
    }

    function handleAnalysisCardEnter (id) {
        const featureId = analysis.featureAnalyses.find(el => el.id === id).featureId;
        const highlight = {
            key: layerSystemName + '_search',
            layerSystemName: layerSystemName,
            features: [{
                featureId: featureId
            }],
            styles: [
                {
                    'type': 'line',
                    'paint': {
                        'line-color': '#A9FE95',
                        'line-width': 10
                    }
                }
            ]
        };
        dispatchHighlightFeature(highlight);
    }

    function handleAnalysisCardLeave (id) {
        const featureId = analysis.featureAnalyses.find(el => el.id === id).featureId;
        const highlight = {
            key: layerSystemName + '_search',
            layerSystemName: layerSystemName
        };
        dispatchUnhighlightFeature(highlight);
    }

    function sort () {
        switch (orderBy) {
            case 1:
                return function (item) { return item.computedData.weightings.totalArea; };
            case 0:
            default:
                return [
                    function (item) { return item.computedData.weightings.totalVeryHighRisk; },   
                    function (item) { return item.computedData.weightings.totalHighRisk; },
                    function (item) { return item.computedData.weightings.totalMediumRisk; },
                    function (item) { return item.computedData.weightings.totalLowRisk; },
                    function (item) { return item.computedData.weightings.totalVeryLowRisk; }
                ];
        }
    } 

    function handleZoomFeature (id) {
        const featureAnalysis = analysis.featureAnalyses.find(el => el.id === id);
        if (featureAnalysis && featureAnalysis.computedData && featureAnalysis.computedData.feature) {
            const coords = featureAnalysis.computedData.feature.bounds.coordinates;
            dispatchSetFitBounds([coords[0][2], coords[0][0]], {
                pitch: 0,
                bearing: 0,
                maxZoom: 16
            });
        }
    }

    const featureAnalyses = analysis.featureAnalyses;
    const featureAnalysesLength = featureAnalyses.length;
    const selectedFeaturesLength = selectedFeatures.length;
    const sortedFeatureAnalyses = order === 0 ? sortBy(featureAnalyses, sort()) : sortBy(featureAnalyses, sort()).reverse();

    return (
        <div className={classes.root}>
            {createSiteCardFeatureAnalysisId !== null && <CreateSiteCardDialog
                open={isCreateSiteCardDialogOpen}
                onClose={closeCreateSiteCardDialog}
                featureConfig={{
                    layerId: layerId
                }}
                featureAnalysisId={createSiteCardFeatureAnalysisId}
                defaultName={featureAnalyses.find(fa => fa.id === createSiteCardFeatureAnalysisId).computedData.feature.primaryLabel}
            />}
            <div className={classes.flex}>
                <Typography color="textPrimary" variant="subtitle1">
                    Results ({featureAnalysesLength}/{analysis.total})
                </Typography>
                <Typography color="textPrimary" variant="subtitle1">
                    Sort by
                </Typography>
            </div>
            <div className={classes.flex}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={featureAnalysesLength > 0 && selectedFeaturesLength === featureAnalysesLength}
                            onChange={handleAllSelected}
                            indeterminate={selectedFeaturesLength !== featureAnalysesLength && selectedFeaturesLength !== 0}
                        />
                    }
                    label={
                        <Typography
                            color="textPrimary"
                        >
                            Select all
                        </Typography>
                    }
                />
                <SimpleSelect
                    value={orderBy}
                    onChange={handleOrderByChange}
                >
                    {[{ id: 0, name: 'Risk' }, { id: 1, name: 'Area' }].map((e, idx) => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
                </SimpleSelect>
                <SimpleSelect
                    value={order}
                    onChange={handleOrderChange}
                >
                    {[{ id: 0, name: 'low to high' }, { id: 1, name: 'high to low' }].map((e, idx) => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
                </SimpleSelect>
            </div>
            <ScrollableContainer className={classes.cards} grow>
                {
                    sortedFeatureAnalyses.map(fa => {
                        const siteCard = siteCards.find(sc => sc.featureId === fa.featureId && sc.layerId === fa.layerId);
                        return (<AnalysisCard
                            key={fa.id}
                            id={fa.id}
                            siteCard={siteCard}
                            data={fa.computedData}

                            onHoverEnter={handleAnalysisCardEnter}
                            onHoverLeave={handleAnalysisCardLeave}
                            onZoomFeature={handleZoomFeature}
                            isSelected={selectedFeatures.includes(fa.id)}
                            onSelectCard={dispatchSetFeatureSelected}
                            onSaveSiteCard={openCreateSiteCardDialog}
                            onViewSiteCard={handleView}
                        />);
                    })
                }
                {
                    [...Array(analysis.total - featureAnalysesLength)].map((e, idx) =>
                        <Skeleton key={idx} animation="wave" height={58} variant="rect" />
                    )
                }
            </ScrollableContainer>
        </div>
    );
}

const styles = theme => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        overflowX: 'hidden',
        overflowY: 'auto'
    },
    flex: {
        display: 'flex',
        alignItems: 'center',
        '& > *:first-child': {
            marginRight: 'auto',
            marginLeft: 0
        }
    },
    cards: {
        '&> *:not(:last-child)':{
            marginBottom: theme.constants.verticalMargin
        },
        '&::-webkit-scrollbar': {
            width: 16
        },
        '&::-webkit-scrollbar-thumb': {
            background: theme.palette.grey.variant3,
            borderLeft: `8px ${theme.palette.background.main} solid`,
            backgroundClip: 'padding-box'
        }
    }
});

export default AnalysisList;